/**
 * The Markdown dialect (SPEC §6.1) → AST. One parser for rendering, refs, renames and search, so
 * what is drawn as a link is exactly what is indexed as a link (v1 had three regex renderers).
 *
 * Block level (per node content): an optional task prefix (TODO / DOING / DONE / CANCELLED),
 * headings `#`–`###`,
 * quotes, code fences, dividers; every other line belongs to a paragraph, and every newline is a
 * line break (as in Logseq).
 * Inline: code spans, `**strong**`, `*em*` / `_em_`, `~~del~~`, `==mark==`, `[text](url)`,
 * `![alt](url)`, bare http(s) URLs, `[[Title]]`, `#tag` / `#[[Two words]]`, `@Name` /
 * `@[[Two words]]`, `((block-id))`, `((t:asset/idx))`, `{{embed ((id))}}`, `{{embed [[Title]]}}`,
 * `{{asset:id}}`, backslash escapes.
 *
 * Safety (SPEC §14): the parser never produces HTML. Link and image targets pass `safeUrl`, so a
 * `javascript:` (or `data:`, relative, obfuscated) target stays plain text. Emphasis follows the
 * CommonMark delimiter algorithm, which is linear in the input.
 *
 * Offsets (`from`, `to`) index into the original content, so refs can be rewritten in place
 * (renames) and the caret can be related to a ref (page creation rules, SPEC §6.2).
 */

/** Task prefixes; CANCELED (Logseq's spelling) is read as CANCELLED. */
export type TaskMarker = 'TODO' | 'DOING' | 'DONE' | 'CANCELLED'
export type RefKind = 'link' | 'tag' | 'mention'

export type Inline =
  | { readonly t: 'text'; readonly v: string; readonly from: number }
  | { readonly t: 'br' }
  | { readonly t: 'code'; readonly v: string }
  | { readonly t: 'strong' | 'em' | 'del' | 'mark'; readonly c: readonly Inline[] }
  | { readonly t: 'link'; readonly href: string; readonly c: readonly Inline[] }
  /** A bare URL; its text is the URL as written. */
  | { readonly t: 'url'; readonly href: string; readonly v: string }
  | { readonly t: 'image'; readonly src: string; readonly alt: string }
  /** `[[T]]` (link), `#t` / `#[[T]]` (tag), `@n` / `@[[N]]` (mention). `title` is as written. */
  | {
      readonly t: 'ref'
      readonly kind: RefKind
      readonly title: string
      readonly bracket: boolean
      readonly from: number
      readonly to: number
    }
  | { readonly t: 'blockref'; readonly id: string; readonly from: number; readonly to: number }
  | { readonly t: 'segref'; readonly asset: string; readonly idx: number; readonly from: number; readonly to: number }
  | {
      readonly t: 'embed'
      readonly id: string | null
      readonly title: string | null
      readonly from: number
      readonly to: number
    }
  | { readonly t: 'asset'; readonly id: string; readonly from: number; readonly to: number }

export type Block =
  | { readonly t: 'p'; readonly c: readonly Inline[] }
  | { readonly t: 'h'; readonly level: 1 | 2 | 3; readonly c: readonly Inline[] }
  | { readonly t: 'quote'; readonly lines: readonly (readonly Inline[])[] }
  | { readonly t: 'pre'; readonly lang: string; readonly v: string }
  | { readonly t: 'hr' }

export interface Doc {
  readonly task: TaskMarker | null
  readonly blocks: readonly Block[]
}

// ------------------------------------------------------------------------------------ URLs

const SAFE_PROTOCOLS: ReadonlySet<string> = new Set(['http:', 'https:', 'mailto:'])
const IMAGE_PROTOCOLS: ReadonlySet<string> = new Set(['http:', 'https:'])

/**
 * The URL if it is absolute and http(s) or mailto, else null. Scheme obfuscation (`java\tscript:`,
 * leading spaces, mixed case) is handled by requiring a clean scheme before WHATWG parsing, which
 * itself strips tabs and newlines and lower-cases the scheme.
 */
export function safeUrl(raw: string, protocols: ReadonlySet<string> = SAFE_PROTOCOLS): string | null {
  const s = raw.trim()
  if (!/^[a-z][a-z0-9+.-]*:/i.test(s)) return null
  let u: URL
  try {
    u = new URL(s)
  } catch {
    return null
  }
  return protocols.has(u.protocol) ? u.href : null
}

/** Bare URLs: GFM extended autolinks, http(s) only, trailing punctuation and unbalanced `)` trimmed. */
function findUrls(src: string, start: number, end: number): { from: number; to: number; href: string }[] {
  const out: { from: number; to: number; href: string }[] = []
  const re = /https?:\/\/[^\s<>]+/giy
  for (let i = src.indexOf('http', start); i >= 0 && i < end; i = src.indexOf('http', i + 1)) {
    if (i > 0 && /[\p{L}\p{N}_]/u.test(src[i - 1]!)) continue
    re.lastIndex = i
    const m = re.exec(src)
    if (!m) continue
    let url = m[0].slice(0, end - i)
    for (;;) {
      const last = url[url.length - 1]
      if (last !== undefined && '?!.,:;*_~\'"'.includes(last)) url = url.slice(0, -1)
      else if (last === ')' && count(url, '(') < count(url, ')')) url = url.slice(0, -1)
      else break
    }
    const href = safeUrl(url)
    if (href !== null && url.length > 'https://'.length - 1) {
      out.push({ from: i, to: i + url.length, href })
      i += url.length - 1
    }
  }
  return out
}

function count(s: string, ch: string): number {
  let n = 0
  for (const c of s) if (c === ch) n++
  return n
}

// ------------------------------------------------------------------------------------ blocks

const TASK = /^(TODO|DOING|DONE|CANCELLED|CANCELED)(?: |$)/
const HEADING = /^(#{1,3}) /
const QUOTE = /^> ?/
const HR = /^(?:-{3,}|\*{3,}|_{3,})[ \t]*$/
const FENCE_OPEN = /^```([^`\s]*)[ \t]*$/
const FENCE_CLOSE = /^```[ \t]*$/

const cache = new Map<string, Doc>()
const CACHE_MAX = 2000

/** Parse a node's content. Results are cached (content strings repeat across renders). */
export function parse(src: string): Doc {
  const hit = cache.get(src)
  if (hit) return hit
  const doc = parseUncached(src)
  if (cache.size >= CACHE_MAX) cache.delete(cache.keys().next().value!)
  cache.set(src, doc)
  return doc
}

function parseUncached(src: string): Doc {
  let pos = 0
  let task: TaskMarker | null = null
  const tm = TASK.exec(src)
  if (tm) {
    task = (tm[1] === 'CANCELED' ? 'CANCELLED' : tm[1]) as TaskMarker
    pos = tm[0].length
  }
  const lines: { from: number; to: number }[] = []
  for (let i = pos; ; ) {
    const nl = src.indexOf('\n', i)
    if (nl < 0) {
      lines.push({ from: i, to: src.length })
      break
    }
    lines.push({ from: i, to: nl })
    i = nl + 1
  }
  const blocks: Block[] = []
  let para: { from: number; to: number } | null = null
  const flush = (): void => {
    if (para) blocks.push({ t: 'p', c: inline(src, para.from, para.to) })
    para = null
  }
  for (let k = 0; k < lines.length; k++) {
    const { from, to } = lines[k]!
    const line = src.slice(from, to)
    const fence = FENCE_OPEN.exec(line)
    if (fence) {
      flush()
      let j = k + 1
      while (j < lines.length && !FENCE_CLOSE.test(src.slice(lines[j]!.from, lines[j]!.to))) j++
      const body = j > k + 1 ? src.slice(lines[k + 1]!.from, lines[Math.min(j, lines.length) - 1]!.to) : ''
      blocks.push({ t: 'pre', lang: fence[1]!, v: body })
      k = j
      continue
    }
    if (HR.test(line)) {
      flush()
      blocks.push({ t: 'hr' })
      continue
    }
    const h = HEADING.exec(line)
    if (h) {
      flush()
      blocks.push({ t: 'h', level: h[1]!.length as 1 | 2 | 3, c: inline(src, from + h[0].length, to) })
      continue
    }
    if (QUOTE.test(line)) {
      flush()
      const quoted: Inline[][] = []
      let j = k
      for (; j < lines.length; j++) {
        const l = lines[j]!
        const q = QUOTE.exec(src.slice(l.from, l.to))
        if (!q) break
        quoted.push(inline(src, l.from + q[0].length, l.to))
      }
      blocks.push({ t: 'quote', lines: quoted })
      k = j - 1
      continue
    }
    if (para) para.to = to
    else para = { from, to }
  }
  flush()
  return { task, blocks }
}

// ------------------------------------------------------------------------------------ inline

interface Delim {
  readonly t: 'delim'
  readonly ch: '*' | '_' | '~' | '='
  /** Characters left in the run (emphasis consumes them from the inside out). */
  n: number
  readonly orig: number
  readonly canOpen: boolean
  readonly canClose: boolean
  readonly from: number
}
type Item = Inline | Delim

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}'
const BLOCKREF = new RegExp(`\\(\\((${UUID})\\)\\)`, 'y')
const SEGREF = new RegExp(`\\(\\(t:(${UUID})/(\\d{1,6})\\)\\)`, 'y')
const EMBED_ID = new RegExp(`\\{\\{[ \\t]*embed[ \\t]+\\(\\((${UUID})\\)\\)[ \\t]*\\}\\}`, 'y')
const EMBED_TITLE = /\{\{[ \t]*embed[ \t]+\[\[([^[\]\n]+)\]\][ \t]*\}\}/y
const ASSET = new RegExp(`\\{\\{[ \\t]*asset:(${UUID})[ \\t]*\\}\\}`, 'y')
const TAG_BODY = /[\p{L}\p{N}\p{M}_\-/+&.]+/uy
const MENTION_BODY = /[\p{L}\p{N}\p{M}_\-.]+/uy
const ALL_DIGITS = /^\p{N}+$/u
const ASCII_PUNCT = /[!-/:-@[-`{-~]/
const PUNCT = /[\p{P}\p{S}]/u
const SPACE = /\s/u
const MAX_NEST = 32
/** Characters where something other than plain text can start. */
const SPECIAL = /[\\`\n[!({#@*_~=]/g

/**
 * True if `#`/`@` at `i` starts a tag or mention: at the start, or after whitespace, an opener
 * or an emphasis delimiter (`**#tag**`). After a letter it is text (`C#`, `anna@example.com`).
 */
function refBoundary(src: string, i: number, start: number): boolean {
  if (i === start) return true
  const p = src[i - 1]!
  return SPACE.test(p) || '([{"\'*_~='.includes(p)
}

/** `[[Title]]` starting at `i` (the first `[`), or null. Titles cannot contain brackets. */
function bracketRef(src: string, i: number, end: number): { title: string; to: number } | null {
  if (src[i] !== '[' || src[i + 1] !== '[') return null
  for (let j = i + 2; j < end; j++) {
    const c = src[j]
    if (c === ']') {
      if (src[j + 1] !== ']' || j + 1 >= end) return null
      const title = src.slice(i + 2, j).trim()
      return title === '' ? null : { title, to: j + 2 }
    }
    if (c === '[' || c === '\n') return null
  }
  return null
}

function sticky(re: RegExp, src: string, i: number, end: number): RegExpExecArray | null {
  re.lastIndex = i
  const m = re.exec(src)
  return m && i + m[0].length <= end ? m : null
}

/** `[text](dest "title")` or `![alt](dest)` at `i` (the `[`). Returns the parts or null. */
function linkAt(src: string, i: number, end: number): { textFrom: number; textTo: number; dest: string; to: number } | null {
  let depth = 0
  let j = i
  for (; j < end; j++) {
    const c = src[j]
    if (c === '\\') {
      j++
      continue
    }
    if (c === '\n' && src[j + 1] === '\n') return null
    if (c === '[') {
      // Nesting is capped (as CommonMark allows), which keeps pathological input linear.
      if (++depth > MAX_NEST) return null
    } else if (c === ']' && --depth === 0) break
  }
  if (j >= end || src[j + 1] !== '(') return null
  let k = j + 2
  while (src[k] === ' ') k++
  let dest: string
  if (src[k] === '<') {
    const close = src.indexOf('>', k)
    if (close < 0 || close >= end || src.slice(k + 1, close).includes('\n')) return null
    dest = src.slice(k + 1, close)
    k = close + 1
  } else {
    let parens = 0
    const s = k
    for (; k < end; k++) {
      const c = src[k]!
      if (c === '\\' && ASCII_PUNCT.test(src[k + 1] ?? '')) {
        k++
        continue
      }
      if (SPACE.test(c) || c < ' ') break
      if (c === '(') {
        if (++parens > MAX_NEST) return null
      } else if (c === ')') {
        if (parens === 0) break
        parens--
      }
    }
    dest = src.slice(s, k).replace(/\\([!-/:-@[-`{-~])/g, '$1')
  }
  while (src[k] === ' ') k++
  const q = src[k]
  if (q === '"' || q === "'") {
    const close = src.indexOf(q, k + 1)
    if (close < 0 || close >= end) return null
    k = close + 1
    while (src[k] === ' ') k++
  }
  if (src[k] !== ')') return null
  return { textFrom: i + 1, textTo: j, dest, to: k + 1 }
}

/** Code span: a backtick run, content up to the next run of the same length (CommonMark). */
function codeSpan(src: string, i: number, end: number): { v: string; to: number } | { skip: number } {
  let n = 0
  while (i + n < end && src[i + n] === '`') n++
  for (let j = src.indexOf('`', i + n); j >= 0 && j < end; ) {
    let m = 0
    while (j + m < end && src[j + m] === '`') m++
    if (m === n) {
      let v = src.slice(i + n, j).replace(/\n/g, ' ')
      if (v.length >= 2 && v[0] === ' ' && v[v.length - 1] === ' ' && v.trim() !== '') v = v.slice(1, -1)
      return { v, to: j + m }
    }
    j = src.indexOf('`', j + m)
  }
  return { skip: n }
}

/** Left/right flanking per CommonMark, with the text edges counting as whitespace. */
function flanking(src: string, from: number, to: number, start: number, end: number): { left: boolean; right: boolean; before: string; after: string } {
  const before = from > start ? src[from - 1]! : ' '
  const after = to < end ? src[to]! : ' '
  const bs = SPACE.test(before)
  const as = SPACE.test(after)
  const bp = PUNCT.test(before)
  const ap = PUNCT.test(after)
  const left = !as && (!ap || bs || bp)
  const right = !bs && (!bp || as || ap)
  return { left, right, before, after }
}

/**
 * Inline content of src[start, end). `plain` (inside link text) allows only code, emphasis and
 * text: no nested links, refs or tags.
 */
function inline(src: string, start: number, end: number, plain = false): Inline[] {
  const items: Item[] = []
  let text = ''
  let textFrom = start
  const addText = (s: string, at: number): void => {
    if (text === '') textFrom = at
    text += s
  }
  const flush = (): void => {
    if (text !== '') items.push({ t: 'text', v: text, from: textFrom })
    text = ''
  }
  const push = (x: Item): void => {
    flush()
    items.push(x)
  }
  const urls = plain ? [] : findUrls(src, start, end)
  let u = 0
  let i = start
  while (i < end) {
    while (u < urls.length && urls[u]!.from < i) u++
    const nextUrl = u < urls.length ? urls[u]!.from : end
    if (nextUrl === i) {
      const x = urls[u++]!
      push({ t: 'url', href: x.href, v: src.slice(x.from, x.to) })
      i = x.to
      continue
    }
    SPECIAL.lastIndex = i
    const sm = SPECIAL.exec(src)
    const next = Math.min(sm ? sm.index : end, nextUrl, end)
    if (next > i) {
      addText(src.slice(i, next), i)
      i = next
      continue
    }
    const c = src[i]!
    switch (c) {
      case '\\': {
        const nx = src[i + 1]
        if (i + 1 < end && nx !== undefined && ASCII_PUNCT.test(nx)) {
          addText(nx, i)
          i += 2
        } else {
          addText('\\', i)
          i++
        }
        continue
      }
      case '`': {
        const r = codeSpan(src, i, end)
        if ('v' in r) {
          push({ t: 'code', v: r.v })
          i = r.to
        } else {
          addText('`'.repeat(r.skip), i)
          i += r.skip
        }
        continue
      }
      case '\n':
        push({ t: 'br' })
        i++
        continue
      case '[': {
        if (!plain) {
          const r = bracketRef(src, i, end)
          if (r) {
            push({ t: 'ref', kind: 'link', title: r.title, bracket: true, from: i, to: r.to })
            i = r.to
            continue
          }
          if (src[i + 1] !== '[') {
            const l = linkAt(src, i, end)
            const href = l ? safeUrl(l.dest) : null
            if (l && href !== null) {
              push({ t: 'link', href, c: inline(src, l.textFrom, l.textTo, true) })
              i = l.to
              continue
            }
          }
        }
        addText('[', i)
        i++
        continue
      }
      case '!': {
        if (!plain && src[i + 1] === '[') {
          const l = linkAt(src, i + 1, end)
          const href = l ? safeUrl(l.dest, IMAGE_PROTOCOLS) : null
          if (l && href !== null) {
            push({ t: 'image', src: href, alt: src.slice(l.textFrom, l.textTo) })
            i = l.to
            continue
          }
        }
        addText('!', i)
        i++
        continue
      }
      case '(': {
        if (!plain && src[i + 1] === '(') {
          const b = sticky(BLOCKREF, src, i, end)
          if (b) {
            push({ t: 'blockref', id: b[1]!, from: i, to: i + b[0].length })
            i += b[0].length
            continue
          }
          const s = sticky(SEGREF, src, i, end)
          if (s) {
            push({ t: 'segref', asset: s[1]!, idx: Number(s[2]), from: i, to: i + s[0].length })
            i += s[0].length
            continue
          }
        }
        addText('(', i)
        i++
        continue
      }
      case '{': {
        if (!plain && src[i + 1] === '{') {
          const e = sticky(EMBED_ID, src, i, end)
          if (e) {
            push({ t: 'embed', id: e[1]!, title: null, from: i, to: i + e[0].length })
            i += e[0].length
            continue
          }
          const et = sticky(EMBED_TITLE, src, i, end)
          if (et && et[1]!.trim() !== '') {
            push({ t: 'embed', id: null, title: et[1]!.trim(), from: i, to: i + et[0].length })
            i += et[0].length
            continue
          }
          const a = sticky(ASSET, src, i, end)
          if (a) {
            push({ t: 'asset', id: a[1]!, from: i, to: i + a[0].length })
            i += a[0].length
            continue
          }
        }
        addText('{', i)
        i++
        continue
      }
      case '#':
      case '@': {
        if (!plain && refBoundary(src, i, start)) {
          const kind: RefKind = c === '#' ? 'tag' : 'mention'
          const r = bracketRef(src, i + 1, end)
          if (r) {
            push({ t: 'ref', kind, title: r.title, bracket: true, from: i, to: r.to })
            i = r.to
            continue
          }
          const body = sticky(c === '#' ? TAG_BODY : MENTION_BODY, src, i + 1, end)
          if (body) {
            const title = body[0].replace(/\.+$/, '')
            if (title !== '' && !ALL_DIGITS.test(title)) {
              push({ t: 'ref', kind, title, bracket: false, from: i, to: i + 1 + title.length })
              i += 1 + title.length
              continue
            }
          }
        }
        addText(c, i)
        i++
        continue
      }
      case '*':
      case '_':
      case '~':
      case '=': {
        let n = 1
        while (i + n < end && src[i + n] === c) n++
        if ((c === '~' || c === '=') && n !== 2) {
          addText(c.repeat(n), i)
          i += n
          continue
        }
        const f = flanking(src, i, i + n, start, end)
        let canOpen = f.left
        let canClose = f.right
        if (c === '_') {
          canOpen = f.left && (!f.right || PUNCT.test(f.before))
          canClose = f.right && (!f.left || PUNCT.test(f.after))
        }
        push({ t: 'delim', ch: c, n, orig: n, canOpen, canClose, from: i })
        i += n
        continue
      }
      default:
        addText(c, i)
        i++
    }
  }
  flush()
  return emphasis(items)
}

// ------------------------------------------------------------------------------------ emphasis

interface LNode {
  v: Item
  prev: LNode | null
  next: LNode | null
  /** Delimiter-stack links (only for nodes whose item is a delimiter still on the stack). */
  prevD: LNode | null
  nextD: LNode | null
}

/** CommonMark "process emphasis" over a linked list, then delimiters left over become text. */
function emphasis(items: Item[]): Inline[] {
  if (!items.some((x) => x.t === 'delim')) return items as Inline[]
  let head: LNode | null = null
  let tail: LNode | null = null
  let lastD: LNode | null = null
  let firstD: LNode | null = null
  for (const v of items) {
    const n: LNode = { v, prev: tail, next: null, prevD: null, nextD: null }
    if (tail) tail.next = n
    else head = n
    tail = n
    if (v.t === 'delim') {
      n.prevD = lastD
      if (lastD) lastD.nextD = n
      else firstD = n
      lastD = n
    }
  }
  const removeD = (n: LNode): void => {
    if (n.prevD) n.prevD.nextD = n.nextD
    else if (firstD === n) firstD = n.nextD
    if (n.nextD) n.nextD.prevD = n.prevD
    n.prevD = n.nextD = null
  }
  const removeNode = (n: LNode): void => {
    if (n.prev) n.prev.next = n.next
    else head = n.next
    if (n.next) n.next.prev = n.prev
  }
  const bottoms = new Map<string, LNode | null>()
  const keyOf = (d: Delim): string => (d.ch === '*' || d.ch === '_' ? `${d.ch}${d.canOpen ? 1 : 0}${d.orig % 3}` : d.ch)

  let closer: LNode | null = firstD
  while (closer) {
    const cd = closer.v as Delim
    if (!cd.canClose) {
      closer = closer.nextD
      continue
    }
    const key = keyOf(cd)
    const bottom = bottoms.has(key) ? bottoms.get(key)! : null
    let opener: LNode | null = closer.prevD
    let found = false
    while (opener && opener !== bottom) {
      const od = opener.v as Delim
      if (od.ch === cd.ch && od.canOpen) {
        const oddMatch =
          (cd.ch === '*' || cd.ch === '_') &&
          (od.canClose || cd.canOpen) &&
          (od.orig + cd.orig) % 3 === 0 &&
          !(od.orig % 3 === 0 && cd.orig % 3 === 0)
        if (!oddMatch) {
          found = true
          break
        }
      }
      opener = opener.prevD
    }
    if (!found || !opener) {
      bottoms.set(key, closer.prevD)
      const next: LNode | null = closer.nextD
      if (!cd.canOpen) removeD(closer)
      closer = next
      continue
    }
    const od = opener.v as Delim
    const use = cd.ch === '~' || cd.ch === '=' ? 2 : od.n >= 2 && cd.n >= 2 ? 2 : 1
    const t = cd.ch === '~' ? 'del' : cd.ch === '=' ? 'mark' : use === 2 ? 'strong' : 'em'
    const inner: Item[] = []
    for (let x = opener.next; x && x !== closer; x = x.next) inner.push(x.v)
    const node: LNode = { v: { t, c: finish(inner) }, prev: opener, next: closer, prevD: null, nextD: null }
    opener.next = node
    closer.prev = node
    // Delimiters between opener and closer are now inside the new node: off the stack.
    opener.nextD = closer
    closer.prevD = opener
    od.n -= use
    cd.n -= use
    if (od.n === 0) {
      removeD(opener)
      removeNode(opener)
    }
    if (cd.n === 0) {
      const next: LNode | null = closer.nextD
      removeD(closer)
      removeNode(closer)
      closer = next
    }
  }
  const out: Item[] = []
  for (let x = head; x; x = x.next) out.push(x.v)
  return finish(out)
}

/** Delimiters that matched nothing become text; adjacent text merges. */
function finish(items: readonly Item[]): Inline[] {
  const out: Inline[] = []
  for (const x of items) {
    const v: Inline = x.t === 'delim' ? { t: 'text', v: x.ch.repeat(x.n), from: x.from } : x
    if (x.t === 'delim' && x.n === 0) continue
    const last = out[out.length - 1]
    if (v.t === 'text' && last?.t === 'text') out[out.length - 1] = { t: 'text', v: last.v + v.v, from: last.from }
    else out.push(v)
  }
  return out
}

// ------------------------------------------------------------------------------------ helpers

/** Every inline node of a document, depth-first (emphasis and link children included). */
export function* walkInlines(doc: Doc): IterableIterator<Inline> {
  function* walk(xs: readonly Inline[]): IterableIterator<Inline> {
    for (const x of xs) {
      yield x
      if (x.t === 'strong' || x.t === 'em' || x.t === 'del' || x.t === 'mark' || x.t === 'link') yield* walk(x.c)
    }
  }
  for (const b of doc.blocks) {
    if (b.t === 'p' || b.t === 'h') yield* walk(b.c)
    else if (b.t === 'quote') for (const l of b.lines) yield* walk(l)
  }
}

/** The text a reader sees, without markup (for labels, breadcrumbs and search). */
export function plainText(src: string): string {
  const doc = parse(src)
  const parts: string[] = []
  const inl = (xs: readonly Inline[]): void => {
    for (const x of xs) {
      switch (x.t) {
        case 'text':
        case 'code':
          parts.push(x.v)
          break
        case 'url':
          parts.push(x.v)
          break
        case 'br':
          parts.push(' ')
          break
        case 'strong':
        case 'em':
        case 'del':
        case 'mark':
        case 'link':
          inl(x.c)
          break
        case 'image':
          parts.push(x.alt)
          break
        case 'ref':
          parts.push(x.kind === 'tag' ? `#${x.title}` : x.kind === 'mention' ? `@${x.title}` : x.title)
          break
        default:
          break
      }
    }
  }
  if (doc.task) parts.push(`${doc.task} `)
  for (const b of doc.blocks) {
    if (b.t === 'p' || b.t === 'h') inl(b.c)
    else if (b.t === 'quote') b.lines.forEach((l, i) => (i && parts.push(' '), inl(l)))
    else if (b.t === 'pre') parts.push(b.v)
    parts.push(' ')
  }
  return parts.join('').replace(/\s+/g, ' ').trim()
}
